import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatHubBranchLabel, filterShipmentLocations } from '../../utils/destinationType.js';
import { IndianRupee, Plus } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import ListPagination from '../common/ListPagination.js';
import { mobileCardClass, listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import ExpenseTrackModal from './ExpenseTrackModal.js';
import { categoriesForOwnBranchAgent } from './ownBranchCategories.js';


const ExpenseList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAgent = user?.role === 'Agent';
  const ownBranchAgent = user?.role === 'Agent'
    && !user?.party_id
    && String(user?.location_hub_ownership || '').toUpperCase() === 'OWN';
  const canCreateExpense = ['Accountant', 'Admin', 'System Admin'].includes(user?.role) || ownBranchAgent;
  const [searchParams] = useSearchParams();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const initialQueue = searchParams.get('queue') || '';
  const initialStatus = searchParams.get('status') || '';
  const initialKind = searchParams.get('kind') || '';
  const [statusFilter, setStatusFilter] = useState(
    ['pending', 'approved', 'rejected', 'paid'].includes(initialStatus)
      ? initialStatus
      : (initialQueue === 'rejected' ? 'rejected' : (initialQueue === 'payment_due' || initialQueue === 'paid' ? 'paid' : 'pending')),
  );
  const [queueFilter, setQueueFilter] = useState(
    ['pending', 'approved', 'rejected', 'paid'].includes(initialStatus) ? '' : initialQueue,
  );
  const [kindFilter, setKindFilter] = useState(
    ['office', 'branch'].includes(initialKind) ? initialKind : '',
  );
  const [statusCounts, setStatusCounts] = useState({
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
  });
  const [trackExpenseId, setTrackExpenseId] = useState(null);
  const [locationHubFilter, setLocationHubFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  const statusTabs = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (categoryFilter) filters.category = categoryFilter;
      if (monthFilter) filters.month = monthFilter;
      if (yearFilter) filters.year = yearFilter;
      if (statusFilter === 'approved_all') {
        filters.approval_bucket = 'approved';
      } else if (statusFilter) {
        filters.status = statusFilter;
      }
      if (queueFilter) filters.queue = queueFilter;
      if (kindFilter) filters.kind = kindFilter;
      if (locationHubFilter) filters.location_hub_id = locationHubFilter;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const response = await expenseService.getAll(currentPage, perPage, filters);
      setExpenses((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
      setTotalPages(response.pagination.pages);
      if (response.status_counts) setStatusCounts(response.status_counts);
    } catch (err) {
      Alert.error('Failed to load expenses');
      console.error(err);
      if (currentPage <= 1) setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, monthFilter, yearFilter, statusFilter, queueFilter, kindFilter, locationHubFilter, startDate, endDate, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchExpenses,
    setCurrentPage,
    currentPage,
    resetPageDeps: [categoryFilter, monthFilter, yearFilter, statusFilter, queueFilter, kindFilter, locationHubFilter, startDate, endDate],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, monthsRes, hubsRes] = await Promise.all([
        expenseService.getCategories(),
        expenseService.getMonths(),
        locationHubService.getHubsForSelect({ brief: 1 }),
      ]);
      const loadedCategories = ownBranchAgent
        ? categoriesForOwnBranchAgent(categoriesRes.data || [])
        : (categoriesRes.data || []);
      setCategories(loadedCategories);
      setMonths(monthsRes.data || []);
      setLocationHubs(filterShipmentLocations(hubsRes.data?.hubs || []));
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  const handleDelete = async (expenseId, expenseName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete expense "${expenseName}"?`,
      'Delete Expense'
    );
    
    if (confirmed) {
      try {
        await expenseService.delete(expenseId);
        Alert.success('Expense deleted successfully!');
        
        if (expenses.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchExpenses();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete expense');
      }
    }
  };

  const handleApprove = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to approve this expense?',
      'Approve Expense'
    );
    
    if (confirmed) {
      try {
        await expenseService.approve(expenseId);
        Alert.success('Expense approved successfully!');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve expense');
      }
    }
  };

  const handleMarkPaid = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Mark this indent as paid?',
      'Pay Indent'
    );
    if (confirmed) {
      try {
        await expenseService.markAsPaid(expenseId, { payment_mode: 'cash' });
        Alert.success('Indent marked as paid');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to mark indent paid');
      }
    }
  };

  const handleReject = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to reject this expense?',
      'Reject Expense'
    );
    if (confirmed) {
      try {
        await expenseService.update(expenseId, { status: 'rejected' });
        Alert.success('Expense rejected successfully!');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject expense');
      }
    }
  };

  const handleSetPending = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to set this expense back to pending?',
      'Set Pending'
    );
    if (confirmed) {
      try {
        await expenseService.update(expenseId, { status: 'pending' });
        Alert.success('Expense set to pending!');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to update expense status');
      }
    }
  };

  const toggleDropdown = (expenseId) => {
    setActiveDropdown(activeDropdown === expenseId ? null : expenseId);
  };

  const isSystemGeneratedExpense = (expense) =>
    ['salary', 'ml_commission', 'ml_commission_deduct'].includes(expense?.source_type);

  const canMutateExpense = (expense) => {
    if (isSystemGeneratedExpense(expense)) return false;
    if (expense.is_indent) return !isAgent && ['Accountant', 'Admin', 'System Admin'].includes(user?.role);
    const stage = expense.approval_stage || 'pending_admin';
    const branch = expense.source_type === 'branch_agent' || expense.expense_kind === 'branch';
    if (user?.role === 'Agent') {
      return branch && expense.status === 'pending' && stage === 'revision_requested';
    }
    if (!['Accountant', 'Admin', 'System Admin'].includes(user?.role)) return false;
    const editable = branch
      ? ['pending_accountant', 'revision_requested']
      : ['pending_admin', 'revision_requested'];
    return expense.status === 'pending' && editable.includes(stage);
  };
  const canApproveExpense = ['Accountant', 'Admin', 'System Admin'].includes(user?.role);

  const getExpenseExtraItems = (expense) => {
    if (isSystemGeneratedExpense(expense)) return [];
    const items = [];
    const role = user?.role;
    if (!expense.is_indent) {
      items.push({ label: 'Track', icon: '↗', onClick: () => setTrackExpenseId(expense.expense_id) });
      return items;
    }
    if (!canMutateExpense(expense)) return [];
    if (expense.is_indent) {
      const stage = expense.approval_stage === 'pending_hub'
        ? 'pending_accountant'
        : (expense.approval_stage || 'pending_accountant');
      const role = user?.role;
      if (stage === 'pending_accountant' && canApproveExpense) {
        items.push({ label: 'Accountant Approve', icon: '✓', onClick: () => handleApprove(expense.expense_id) });
      }
      if (stage === 'pending_admin' && ['Admin', 'System Admin'].includes(role)) {
        items.push({ label: 'Admin Approve', icon: '✓', onClick: () => handleApprove(expense.expense_id) });
      }
      if (stage === 'approved_for_payment' && (user?.can_execute_payments || role === 'System Admin')) {
        items.push({
          label: 'Mark Paid',
          icon: '₹',
          onClick: () => handleMarkPaid(expense.expense_id),
        });
      }
      if ((expense.status === 'pending' || expense.status === 'approved') && canApproveExpense) {
        items.push({ label: 'Reject', icon: '✗', onClick: () => handleReject(expense.expense_id) });
      }
      return items;
    }
    if (!canApproveExpense) return [];
    if (expense.status === 'pending') {
      items.push({ label: 'Approve', icon: '✓', onClick: () => handleApprove(expense.expense_id) });
    }
    if (expense.status === 'pending' || expense.status === 'approved') {
      items.push({ label: 'Reject', icon: '✗', onClick: () => handleReject(expense.expense_id) });
    }
    if (expense.status === 'rejected') {
      items.push({ label: 'Set Pending', icon: '↩', onClick: () => handleSetPending(expense.expense_id) });
    }
    return items;
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setMonthFilter('');
    setYearFilter('');
    setStatusFilter('pending');
    setQueueFilter('');
    setKindFilter('');
    setLocationHubFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = categoryFilter || monthFilter || yearFilter || statusFilter !== 'pending' || queueFilter || kindFilter || locationHubFilter || startDate || endDate;

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700';
  };

  const expenseKindLabel = (expense) => {
    if (expense.expense_kind === 'branch' || expense.source_type === 'branch_agent') return 'Own branch';
    if (expense.expense_kind === 'system') return '';
    return 'Office';
  };

  const expenseStatusLabel = (expense) => {
    const branch = expense.expense_kind === 'branch' || expense.source_type === 'branch_agent';
    if (branch) {
      if (expense.status === 'rejected') return 'Rejected';
      if (expense.status === 'approved' || expense.status === 'paid' || expense.approval_stage === 'settled') return 'Settled';
      const branchLabels = {
        pending_accountant: 'Pending accountant',
        pending_admin: 'Pending admin',
        revision_requested: 'Sent back',
      };
      return branchLabels[expense.approval_stage] || 'Pending';
    }
    if (
      expense.is_indent
      && expense.approval_stage
      && expense.status !== 'paid'
      && expense.status !== 'rejected'
      && expense.status !== 'cancelled'
    ) {
      const stageLabels = {
        pending_hub: 'Indent: Accountant',
        pending_accountant: 'Indent: Accountant',
        pending_admin: 'Indent: Admin',
        approved_for_payment: 'Indent: Ready to pay',
      };
      return stageLabels[expense.approval_stage] || expense.status_display;
    }
    if (expense.status === 'paid' || expense.approval_stage === 'paid') return 'Payment done';
    const officeLabels = {
      pending_admin: 'Pending admin',
      pending_owner: 'Pending owner',
      revision_requested: 'Sent back',
      approved: 'Payment due',
      rejected: 'Rejected',
    };
    if (officeLabels[expense.approval_stage] && expense.status !== 'paid' && expense.status !== 'cancelled') {
      if (expense.status === 'approved') return 'Payment due';
      return officeLabels[expense.approval_stage];
    }
    if (expense.status === 'approved') return 'Payment due';
    return expense.status_display;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <DashboardLayout>
              <div className="w-full min-w-0 max-w-full mx-auto">
                <ListPageHeader
                  title="Expenses"
                  icon={IndianRupee}
                  titleClassName="text-secondary"
                  iconClassName="w-5 h-5 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-4"
                >
                  {canCreateExpense && (
                    <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/expenses/add')}>
                      Add Expense
                    </Button>
                  )}
                </ListPageHeader>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {statusTabs.map((tab) => {
                    const active = tab.key === 'approved'
                      ? ['approved', 'approved_all', 'paid'].includes(statusFilter) && !queueFilter
                      : statusFilter === tab.key && !queueFilter;
                    const row = statusCounts[tab.key] || { count: 0, amount: 0 };
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setStatusFilter(tab.key === 'approved' ? 'approved_all' : tab.key);
                          setQueueFilter('');
                        }}
                        className={`text-left rounded-xl border px-4 py-3 ${active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{tab.label}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{row.count || 0}</p>
                        <p className="text-xs text-gray-600">₹{Number(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </button>
                    );
                  })}
                </div>

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Type</label>
                    <select
                      value={kindFilter}
                      onChange={(e) => setKindFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All types</option>
                      <option value="office">Office</option>
                      <option value="branch">Own branch</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Stage</label>
                    <select
                      value={queueFilter}
                      onChange={(e) => setQueueFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All stages</option>
                      <option value="pending_accountant">Pending accountant</option>
                      <option value="pending_admin">Pending admin</option>
                      <option value="pending_owner">Pending owner</option>
                      <option value="revision_requested">Sent back</option>
                      <option value="payment_due">Approved (payment due)</option>
                      <option value="paid">Payment done</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Category</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Month</label>
                    <select
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Months</option>
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Year</label>
                    <select
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Years</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Location Hub</label>
                    <select
                      value={locationHubFilter}
                      onChange={(e) => setLocationHubFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Locations</option>
                      {locationHubs.map((hub) => (
                        <option key={hub.id} value={hub.id}>
                          {formatHubBranchLabel(hub) || hub.location}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>
                </ListFilterPanel>

                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading expenses..." />
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">No expenses found</div>
                ) : (
                  <>
                    {/* Table - Desktop View */}
                    <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
                      <div className="w-full overflow-x-auto overscroll-x-contain">
                        <table className="w-full min-w-[1100px] border-collapse divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Expense ID</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Category</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Location Hub</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[120px]">Vendor</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">{t('common.createdBy')}</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                            </tr>
                          </thead>

                          <tbody className="bg-white divide-y divide-gray-200">
                            {expenses.map((expense) => (
                              <tr
                                key={expense.expense_id}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => setTrackExpenseId(expense.expense_id)}
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={listIdLinkClass}>
                                      {expense.expense_id}
                                    </span>
                                  </td>

                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{expense.category_display}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 min-w-[140px]">
                                  <span className="block truncate max-w-[180px]" title={expense.location_hub_name || ''}>
                                    {expense.location_hub_name || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(expense.expense_date)}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{formatAmount(expense.amount)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 min-w-[120px]">
                                  <span className="block truncate max-w-[160px]">{expense.vendor_name || 'N/A'}</span>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`px-3 py-1 text-sm rounded-full ${getStatusBadgeClass(expense.status)}`}>
                                    {expenseStatusLabel(expense)}
                                  </span>
                                  {expenseKindLabel(expense) ? (
                                    <p className="text-[11px] text-gray-500 mt-1">{expenseKindLabel(expense)}</p>
                                  ) : null}
                                </td>

                                <td className="px-4 py-3 min-w-[140px]">
                                  <div className="max-w-[180px]">
                                    <p
                                      className="truncate text-sm font-medium text-gray-800"
                                      title={expense.created_by_name || expense.created_by || ''}
                                    >
                                      {expense.created_by_name || expense.created_by || 'N/A'}
                                    </p>
                                    {(expense.location_hub_name || expense.created_by_location_hub) && (
                                      <p
                                        className="truncate text-xs text-gray-500 mt-0.5"
                                        title={expense.location_hub_name || expense.created_by_location_hub}
                                      >
                                        {expense.location_hub_name || expense.created_by_location_hub}
                                      </p>
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap relative" onClick={(event) => event.stopPropagation()}>
                                    <RowActionsMenu
                                      isOpen={activeDropdown === expense.expense_id}
                                      onToggle={() => toggleDropdown(expense.expense_id)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                      onEdit={canMutateExpense(expense) ? () => navigate(`/expenses/edit/${expense.expense_id}`) : undefined}
                                      onDelete={canMutateExpense(expense) ? () => handleDelete(expense.expense_id, expense.category_display) : undefined}
                                      extraItems={getExpenseExtraItems(expense)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="xl:hidden space-y-3 sm:space-y-4">
                      {expenses.map((expense) => (
                        <div
                          key={expense.expense_id}
                          className={`${mobileCardClass} sm:p-5 cursor-pointer`}
                          onClick={() => setTrackExpenseId(expense.expense_id)}
                        >
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <h3 className={listIdLinkMobileClass}>
                                {expense.expense_id}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600">{expense.category_display}</p>
                              <p className="text-lg sm:text-xl font-bold text-gray-900 mt-1 md:hidden">{formatAmount(expense.amount)}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0" onClick={(event) => event.stopPropagation()}>
                              <span className="inline-flex flex-col items-end gap-1">
                                <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadgeClass(expense.status)}`}>
                                  {expenseStatusLabel(expense)}
                                </span>
                                {expenseKindLabel(expense) ? (
                                  <span className="text-[10px] text-gray-500">{expenseKindLabel(expense)}</span>
                                ) : null}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === expense.expense_id}
                                onToggle={() => toggleDropdown(expense.expense_id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                onEdit={canMutateExpense(expense) ? () => navigate(`/expenses/edit/${expense.expense_id}`) : undefined}
                                onDelete={canMutateExpense(expense) ? () => handleDelete(expense.expense_id, expense.category_display) : undefined}
                                extraItems={getExpenseExtraItems(expense)}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div className="hidden md:block">
                              <span className="text-gray-500">Amount:</span>
                              <p className="text-gray-900 font-medium">{formatAmount(expense.amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="text-gray-900 font-medium">{formatDate(expense.expense_date)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Location Hub:</span>
                              <p className="text-gray-900 font-medium">{expense.location_hub_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Vendor:</span>
                              <p className="text-gray-900 font-medium">{expense.vendor_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Month/Year:</span>
                              <p className="text-gray-900 font-medium">{expense.month_year}</p>
                            </div>
                          </div>
                        </div>
                      ))}

                    </div>
                    <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      loading={loading}
                      onNext={() => setCurrentPage((page) => page + 1)}
                    />
                  </>
                )}
              </div>
      {trackExpenseId && (
        <ExpenseTrackModal
          expenseId={trackExpenseId}
          user={user}
          onClose={() => setTrackExpenseId(null)}
          onChanged={fetchExpenses}
        />
      )}
    </DashboardLayout>
  );
};

export default ExpenseList;